import { useCallback, useState } from "react";

/** A localStorage-backed object of preferences.
 *
 *  Shared by settings.ts and filters.ts. Both are device-local by design —
 *  they're per-person display choices, not shared ward data, so the clerk
 *  changing a threshold shouldn't change what a counsellor sees.
 */
export function usePersisted<T extends object>(key: string, defaults: T) {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return defaults;
      // Merged over the defaults so a newly added field doesn't come back
      // undefined for anyone with an existing stored object.
      return { ...defaults, ...(JSON.parse(raw) as Partial<T>) };
    } catch {
      // Private-mode Safari throws on localStorage access. Defaults are fine.
      return defaults;
    }
  });

  const write = useCallback(
    (next: T) => {
      setValue(next);
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        // Not persisting is survivable; losing the interaction is not.
      }
    },
    [key],
  );

  const set = useCallback(
    <K extends keyof T>(field: K, v: T[K]) => setValue((prev) => {
      const next = { ...prev, [field]: v };
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch { /* see above */ }
      return next;
    }),
    [key],
  );

  const reset = useCallback(() => write(defaults), [write, defaults]);

  return { value, set, reset };
}
